import { createORPCClient, createSafeClient, ORPCError, onError, type ClientContext } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { ApiClient } from "@umi/api/orpc/client";
import pDebounce from "p-debounce";


// isDefinedError narrows to declared procedure errors; a global interceptor
// has no procedure, so that union is empty (never). ORPCError is the
// instance check that still exposes code for UNAUTHORIZED / TOO_MANY_REQUESTS.
export const isUnauthorizedError = (error: unknown) => {
  return error instanceof ORPCError
    && error.defined
    && error.code === "UNAUTHORIZED";
};

export const isTooManyRequestsError = (error: unknown) => {
  return error instanceof ORPCError
    && error.defined
    && error.code === "TOO_MANY_REQUESTS";
};

// Parallel 401s from oRPC should produce a single toast, same as v3 api.ts.
const notifySessionExpired = pDebounce(() => {
  showInfoNotification("Your session has expired. Please log in again.");
}, 100);

const notifyRateLimited = pDebounce(() => {
  showInfoNotification("Too many requests. Please wait a moment and try again.");
}, 100);

let link: RPCLink<ClientContext> | null = null;


const createRPCLink = () => {
  const config = useRuntimeConfig();

  return new RPCLink({
    url: config.public.apiUrl + "/orpc",
    headers: () => {
      const authStore = useAuthStore();
      if (!authStore.jwt) return {};
      return { Authorization: `Bearer ${authStore.jwt}` };
    },
    interceptors: [
      onError(async (error) => {
        if (isTooManyRequestsError(error)) {
          suppressErrorNotification();
          notifyRateLimited();
          return;
        }
        if (!isUnauthorizedError(error)) return;
        const authStore = useAuthStore();
        if (!authStore.jwt) return;
        await authStore.logout();
        notifySessionExpired();
      }),
    ]
  });
};


export const useORPC = () => {
  link ??= createRPCLink();
  return createORPCClient<ApiClient>(link);
};

export const useSafeORPC = () => {
  return createSafeClient(useORPC());
};
